import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CreateCompanyForm } from "@/components/companies/CreateCompanyForm";
import { useActiveTeam, useCanWriteCompanies } from "@/lib/team-context";

export const Route = createFileRoute("/companies/new")({
  component: NewCompanyPage,
});

function NewCompanyPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewCompanyContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewCompanyContent() {
  const { t } = useTranslation();
  const { activeTeam, ready: teamReady } = useActiveTeam();
  const canCreate = useCanWriteCompanies();

  if (teamReady && !activeTeam) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <h2 className="text-lg font-semibold">
              {t("companies.no_team.title")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("companies.no_team.body")}
            </p>
            <Button asChild>
              <Link to="/teams">{t("companies.no_team.cta")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The API refuses a viewer's create; don't offer a form it can't save.
  if (activeTeam && !canCreate) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("companies.read_only.title")}
          description={t("companies.read_only.body")}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div>
        <Link
          to="/companies"
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("companies.create.back")}
        </Link>
      </div>
      <CreateCompanyForm />
    </div>
  );
}
